import { ModulePermissionGroup, PermissionItem } from "./role.dto";

/**
 * Complete KwikMemories Studio RBAC Catalog
 * 15 Modules, 55 Permission keys
 * 
 * Note: The backend API (GET /account/studio/permissions/) is the authority.
 * This catalog serves as the structured UI specification and offline fallback for labels/descriptions.
 */
export const MODULE_NAMES = [
  "DASHBOARD",
  "SUBSCRIPTIONS",
  "ALBUMS",
  "MEMBERS",
  "ROLE_MANAGEMENT",
  "EVENTS",
  "PAYMENTS_MANAGEMENT",
  "PACKAGES",
  "QUOTATION",
  "ORDERS",
  "INVOICE",
  "WHATSAPP_CRM",
  "LEADS",
  "EQUIPMENT",
  "CAMERA_TO_CLOUD",
  "MARKETPLACE",
] as const;

export type ModuleName = typeof MODULE_NAMES[number];

export const getModuleOrderIndex = (moduleName: string): number => {
  const idx = MODULE_NAMES.indexOf(moduleName as any);
  return idx === -1 ? 999 : idx;
};

export const MODULE_LABEL_MAP: Record<string, string> = {
  DASHBOARD: "Dashboard",
  SUBSCRIPTIONS: "Subscriptions",
  ALBUMS: "Albums",
  MEMBERS: "Members",
  ROLE_MANAGEMENT: "Role Management",
  EVENTS: "Events",
  PAYMENTS_MANAGEMENT: "Payments Management",
  PACKAGES: "Packages",
  QUOTATION: "Quotation",
  ORDERS: "Orders",
  INVOICE: "Invoice",
  WHATSAPP_CRM: "WhatsApp CRM",
  LEADS: "Leads CRM",
  EQUIPMENT: "Equipment",
  CAMERA_TO_CLOUD: "Camera to Cloud",
  MARKETPLACE: "Marketplace",
};

export const FALLBACK_PERMISSIONS_CATALOG: ModulePermissionGroup[] = [
  {
    module: "DASHBOARD",
    label: "Dashboard",
    permissions: [
      { key: "dashboard.view", label: "View Analytics & Dashboard", description: "Access main studio analytics and dashboard metrics" },
    ],
  },
  {
    module: "SUBSCRIPTIONS",
    label: "Subscriptions",
    permissions: [
      { key: "subscriptions.read", label: "View Subscription", description: "View current studio subscription plan and limits" },
    ],
  },
  {
    module: "ALBUMS",
    label: "Albums",
    permissions: [
      { key: "albums.view", label: "View Albums", description: "Browse albums, folders, and photos" },
      { key: "albums.create", label: "Create Album", description: "Create new albums and projects" },
      { key: "albums.update", label: "Update Album", description: "Edit album details and folder settings" },
      { key: "albums.create_folder", label: "Create Folder", description: "Create sub-folders inside albums" },
      { key: "albums.upload_media", label: "Upload Media", description: "Upload photos and videos to albums" },
      { key: "albums.edit_delete_media", label: "Edit / Delete Media", description: "Edit media tags, delete photos and media" },
      { key: "albums.download_media", label: "Download Media", description: "Download photos and full album archives" },
      { key: "albums.lock", label: "Lock / Unlock", description: "Lock or unlock albums with security pin" },
      { key: "albums.activate_media_pricing", label: "Media Pricing", description: "Enable image purchase and set media prices" },
      { key: "albums.share_album_code", label: "Share Album Code", description: "Generate and share QR/album access codes" },
      { key: "albums.redeem_code_users", label: "Redeem Users", description: "View list of customers who redeemed album codes" },
      { key: "albums.hide", label: "Hide / Unhide", description: "Hide albums and folders from customer view" },
      { key: "albums.activate_watermark", label: "Watermark", description: "Apply and configure photo watermarking" },
      { key: "albums.delete", label: "Delete Album", description: "Permanently delete albums and all content" },
    ],
  },
  {
    module: "MEMBERS",
    label: "Members",
    permissions: [
      { key: "members.read", label: "View Members", description: "View team members and profiles" },
      { key: "members.create", label: "Add Member", description: "Add new team members to the studio" },
      { key: "members.update", label: "Update Member", description: "Edit team member details and roles" },
      { key: "members.delete", label: "Delete Member", description: "Remove team members from the studio" },
    ],
  },
  {
    module: "ROLE_MANAGEMENT",
    label: "Role Management",
    permissions: [
      { key: "role_management.read", label: "View Roles", description: "View studio roles and permission catalog" },
      { key: "role_management.create", label: "Create Role", description: "Create new custom roles and permissions" },
      { key: "role_management.update", label: "Update / Assign Role", description: "Edit roles and assign roles to team members" },
      { key: "role_management.delete", label: "Delete Role", description: "Delete studio roles" },
    ],
  },
  {
    module: "EVENTS",
    label: "Events",
    permissions: [
      { key: "events.read", label: "View Events", description: "View event calendar and customer inquiries" },
      { key: "events.create", label: "Create Event", description: "Schedule and create new studio events" },
      { key: "events.update", label: "Update Event", description: "Edit event schedules and sub-events" },
      { key: "events.delete", label: "Delete Event", description: "Delete scheduled events and sub-events" },
    ],
  },
  {
    module: "PAYMENTS_MANAGEMENT",
    label: "Payments Management",
    permissions: [
      { key: "payments_management.read", label: "View Payments", description: "View team payments and customer payment links" },
      { key: "payments_management.create", label: "Create Payments", description: "Generate payment links and payouts" },
      { key: "payments_management.update", label: "Update Payments", description: "Edit payment status and installments" },
      { key: "payments_management.delete", label: "Delete Payments", description: "Delete payment records" },
    ],
  },
  {
    module: "PACKAGES",
    label: "Packages",
    permissions: [
      { key: "packages.read", label: "View Packages", description: "View business packages and booking requests" },
      { key: "packages.create", label: "Create Package", description: "Create new business packages" },
      { key: "packages.update", label: "Update Package", description: "Edit package details, inclusions, and prices" },
      { key: "packages.delete", label: "Delete Package", description: "Delete packages" },
    ],
  },
  {
    module: "QUOTATION",
    label: "Quotation",
    permissions: [
      { key: "quotation.read", label: "View Quotations", description: "View quotation list, details, and products" },
      { key: "quotation.create", label: "Create Quotation", description: "Create quotes, products, and charge rules" },
      { key: "quotation.update", label: "Update Quotation", description: "Edit quotes, change status, and convert to orders" },
      { key: "quotation.delete", label: "Delete Quotation", description: "Delete quotations and quotation items" },
    ],
  },
  {
    module: "ORDERS",
    label: "Orders",
    permissions: [
      { key: "orders.read", label: "View Orders", description: "View orders, sessions, and assignments" },
      { key: "orders.create", label: "Create Order", description: "Create new orders and work items" },
      { key: "orders.update", label: "Update Order", description: "Edit orders, update stages, and assign crew" },
      { key: "orders.delete", label: "Delete Order", description: "Delete orders and transactions" },
    ],
  },
  {
    module: "INVOICE",
    label: "Invoice",
    permissions: [
      { key: "invoice.read", label: "View Invoices", description: "View invoice dashboard, invoices, and details" },
      { key: "invoice.create", label: "Create Invoice", description: "Generate and create new invoices" },
      { key: "invoice.update", label: "Update Invoice", description: "Edit invoice details, items, and status" },
      { key: "invoice.delete", label: "Delete Invoice", description: "Delete invoices" },
    ],
  },
  {
    module: "WHATSAPP_CRM",
    label: "WhatsApp CRM",
    permissions: [
      { key: "whatsapp_crm.full", label: "Full WhatsApp CRM", description: "Access templates, campaigns, chats, and automations" },
    ],
  },
  {
    module: "LEADS",
    label: "Leads CRM",
    permissions: [
      { key: "leads.read", label: "View Leads", description: "View leads, activities, and pipeline stats" },
      { key: "leads.create", label: "Create Lead", description: "Add new leads and inquiries" },
      { key: "leads.update", label: "Update Lead", description: "Update lead stages, notes, and AI scoring" },
      { key: "leads.delete", label: "Delete Lead", description: "Delete lead records" },
    ],
  },
  {
    module: "EQUIPMENT",
    label: "Equipment",
    permissions: [
      { key: "equipment.read", label: "View Equipment", description: "View equipment items, categories, and assignments" },
      { key: "equipment.create", label: "Add Equipment", description: "Add equipment items, categories, and reminders" },
      { key: "equipment.update", label: "Update Equipment", description: "Assign, return, maintain, and edit gear" },
      { key: "equipment.delete", label: "Delete Equipment", description: "Delete equipment items and categories" },
    ],
  },
  {
    module: "CAMERA_TO_CLOUD",
    label: "Camera to Cloud",
    permissions: [
      { key: "camera_to_cloud.full", label: "Full Camera to Cloud", description: "Manage live C2C upload sessions and credentials" },
    ],
  },
  {
    module: "MARKETPLACE",
    label: "Marketplace",
    permissions: [
      { key: "marketplace.crud", label: "Marketplace Studio", description: "Manage studio listings, bookings, and gear visibility" },
    ],
  },
];

/**
 * Normalizes backend catalog payload into ModulePermissionGroup[]
 */
export function normalizeCatalog(backendCatalog: any): ModulePermissionGroup[] {
  if (!backendCatalog) return FALLBACK_PERMISSIONS_CATALOG;

  // Extract array of module groups if nested under .data.modules, .modules, or .data
  const rawList: any[] | null = Array.isArray(backendCatalog)
    ? backendCatalog
    : Array.isArray(backendCatalog?.data?.modules)
    ? backendCatalog.data.modules
    : Array.isArray(backendCatalog?.modules)
    ? backendCatalog.modules
    : Array.isArray(backendCatalog?.data)
    ? backendCatalog.data
    : null;

  // Case 1: Backend returns array of ModulePermissionGroup
  if (rawList && rawList.length > 0 && rawList[0].module) {
    const mapped = rawList.map((group) => {
      const moduleKey = String(group.module).toUpperCase();
      const fallbackGroup = FALLBACK_PERMISSIONS_CATALOG.find((g) => g.module === moduleKey);
      return {
        module: moduleKey,
        label: group.label || MODULE_LABEL_MAP[moduleKey] || group.sidebar_item || moduleKey,
        permissions: (group.permissions || []).map((p: any) => {
          if (typeof p === "string") {
            const fallbackP = fallbackGroup?.permissions.find((fp) => fp.key === p);
            return {
              key: p,
              label: fallbackP?.label || p.split(".").pop() || p,
              description: fallbackP?.description,
            };
          }
          const keyStr = p.key || p.permission_key || p.name;
          const fallbackP = fallbackGroup?.permissions.find((fp) => fp.key === keyStr);
          return {
            key: keyStr,
            label: fallbackP?.label || p.label || p.name || keyStr,
            description: fallbackP?.description || p.description,
          };
        }),
      };
    });
    return mapped.sort((a, b) => getModuleOrderIndex(a.module) - getModuleOrderIndex(b.module));
  }

  // Case 2: Backend returns an object with modules as keys: { ALBUMS: ["albums.view", ...], ... }
  if (typeof backendCatalog === "object" && !Array.isArray(backendCatalog)) {
    const rawObj = backendCatalog.data || backendCatalog.modules || backendCatalog;
    if (typeof rawObj === "object" && !Array.isArray(rawObj)) {
      const result: ModulePermissionGroup[] = [];
      for (const [modKey, perms] of Object.entries(rawObj)) {
        const moduleUpper = modKey.toUpperCase();
        const fallbackGroup = FALLBACK_PERMISSIONS_CATALOG.find((g) => g.module === moduleUpper);
        if (Array.isArray(perms)) {
          result.push({
            module: moduleUpper,
            label: MODULE_LABEL_MAP[moduleUpper] || moduleUpper,
            permissions: perms.map((p: any) => {
              const keyStr = typeof p === "string" ? p : p.key || p.name;
              const fallbackP = fallbackGroup?.permissions.find((fp) => fp.key === keyStr);
              return {
                key: keyStr,
                label: typeof p === "object" && p.label ? p.label : (fallbackP?.label || keyStr.split(".").pop() || keyStr),
                description: typeof p === "object" && p.description ? p.description : fallbackP?.description,
              };
            }),
          });
        }
      }
      if (result.length > 0) {
        return result.sort((a, b) => getModuleOrderIndex(a.module) - getModuleOrderIndex(b.module));
      }
    }
  }

  return [...FALLBACK_PERMISSIONS_CATALOG].sort((a, b) => getModuleOrderIndex(a.module) - getModuleOrderIndex(b.module));
}

/**
 * Resolves the primary "View" / "Read" baseline permission for a given module.
 * Sub-users require this permission granted to access the module or see it in the sidebar.
 */
export function getModuleViewPermission(
  group?: ModulePermissionGroup | null
): PermissionItem | undefined {
  if (!group || !group.permissions || group.permissions.length === 0) return undefined;

  // 1. Look for explicit .view or .read key endings (e.g. albums.view, leads.read)
  const byKey = group.permissions.find((p) => {
    const key = p.key.toLowerCase();
    return (
      key.endsWith(".view") ||
      key.endsWith(".read") ||
      key.endsWith(".list") ||
      key.includes(".view_") ||
      key.includes(".read_")
    );
  });
  if (byKey) return byKey;

  // 2. Look by human label starting with "view" or "read"
  const byLabel = group.permissions.find((p) => {
    const label = (p.label || "").toLowerCase().trim();
    return label.startsWith("view") || label.startsWith("read");
  });
  if (byLabel) return byLabel;

  // 3. If module has only 1 permission, that single permission is its entry point
  if (group.permissions.length === 1) {
    return group.permissions[0];
  }

  // 4. Default fallback to first permission in module
  return group.permissions[0];
}

/**
 * Checks whether a specific permission key is the view permission for a given module.
 */
export function isModuleViewPermission(
  permKey: string,
  group?: ModulePermissionGroup | null
): boolean {
  if (!permKey) return false;
  if (group) {
    const viewPerm = getModuleViewPermission(group);
    return viewPerm?.key === permKey;
  }
  const key = permKey.toLowerCase();
  return key.endsWith(".view") || key.endsWith(".read") || key.endsWith(".list");
}
